import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Search, Brain, FileSearch, Lightbulb, ArrowRight } from 'lucide-react';
import * as THREE from 'three';
// @ts-expect-error - vanta has no bundled type declarations
import NET from 'vanta/dist/vanta.net.min';
import type { VantaEffect } from '../types/vanta';

const steps = [
  {
    icon: Search,
    title: 'Detect',
    question: 'What changed?',
    description: 'Surface the change or event worth paying attention to.',
  },
  {
    icon: Brain,
    title: 'Understand',
    question: 'Why did it happen?',
    description: "Connect the event to the infrastructure state around it.",
  },
  {
    icon: FileSearch,
    title: 'Investigate',
    question: 'What is affected?',
    description: 'Trace the blast radius across resources, deployments, and services.',
  },
  {
    icon: Lightbulb,
    title: 'Recommend',
    question: 'What should I check next?',
    description: 'Suggest the safest next step — the operator stays in control of what happens.',
  },
];

const AIOperations: React.FC = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const backgroundRef = useRef<HTMLDivElement>(null);
  const vantaEffectRef = useRef<VantaEffect | null>(null);

  useEffect(() => {
    if (!backgroundRef.current || vantaEffectRef.current) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    vantaEffectRef.current = NET({
      el: backgroundRef.current,
      THREE,
      mouseControls: false,
      touchControls: false,
      gyroControls: false,
      minHeight: 200.0,
      minWidth: 200.0,
      scale: 1.0,
      scaleMobile: 1.0,
      color: 0x38bdf8,
      backgroundAlpha: 0,
      points: 7.0,
      maxDistance: 22.0,
      spacing: 18.0,
      showDots: true,
    });

    return () => {
      vantaEffectRef.current?.destroy();
      vantaEffectRef.current = null;
    };
  }, []);

  return (
    <section id="ai-operations" className="py-32 relative overflow-hidden bg-dark-900">
      {/* Animated infrastructure network - a subtle backdrop, not the focal point */}
      <div ref={backgroundRef} className="absolute inset-0 pointer-events-none" />
      <div className="absolute inset-0 bg-gradient-to-b from-dark-900/60 via-dark-900/80 to-dark-900" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-6xl font-semibold mb-6">
            <span className="text-white">From dashboards</span>
            <br />
            <span className="gradient-text">to decisions</span>
          </h2>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            Traditional DevOps tools show engineers what happened. DeploAI's AI operations layer
            is designed to help answer what changed, why it happened, what's affected, and what to
            check next — grounded in your actual infrastructure state, not a generic chatbot.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="flex flex-col lg:flex-row items-stretch justify-center gap-4 mb-10"
        >
          {steps.map((step, index) => (
            <React.Fragment key={step.title}>
              <div className="feature-card flex-1 text-center">
                <div className="w-14 h-14 bg-gradient-to-br from-primary-500 to-neon-blue rounded-xl flex items-center justify-center mx-auto mb-4 neon-glow">
                  <step.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-1">{step.title}</h3>
                <p className="text-sm text-neon-blue mb-3">&ldquo;{step.question}&rdquo;</p>
                <p className="text-sm text-gray-400">{step.description}</p>
              </div>
              {index < steps.length - 1 && (
                <div className="hidden lg:flex items-center text-gray-600">
                  <ArrowRight className="w-5 h-5" />
                </div>
              )}
            </React.Fragment>
          ))}
        </motion.div>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="text-center text-gray-500 text-sm"
        >
          Automated remediation ("Act") is on the roadmap and will only run within explicit
          permissions once available — it is not part of the platform today.
        </motion.p>
      </div>
    </section>
  );
};

export default AIOperations;
